import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarDays, Send, CheckCircle, XCircle } from 'lucide-react';
import { supabaseClient } from '../lib/supabaseClient';
import { PreferenceType, PREFERENCE_TYPE_LABELS, toShiftPreference } from '../types/shift';

interface Employee {
  id: string;
  name: string;
}

interface ShiftSettings {
  deadline_day: number;
}

const PREFERENCE_STYLES: Record<PreferenceType, string> = {
  paid: 'bg-yellow-200 text-yellow-900 border-yellow-400',
  dayoff: 'bg-gray-200 text-gray-700 border-gray-400',
  night_request: 'bg-purple-200 text-purple-900 border-purple-400',
};

const PREFERENCE_CYCLE: (PreferenceType | null)[] = [null, 'dayoff', 'night_request', 'paid'];

const getDaysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();
const getFirstDayOfWeek = (year: number, month: number): number => new Date(year, month - 1, 1).getDay();

const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
const DOW_COLORS = ['text-red-500', '', '', '', '', '', 'text-blue-500'];

const ShiftRequest: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [settings, setSettings] = useState<ShiftSettings | null>(null);
  const [preferences, setPreferences] = useState<Map<string, PreferenceType>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const computedTarget = useMemo(() => {
    const deadlineDay = settings?.deadline_day ?? 15;
    const current = new Date();
    const currentYear = current.getFullYear();
    const currentMonth = current.getMonth() + 1;

    // 締切前: 翌月分を編集可能 / 締切後: 翌々月分を編集可能
    const monthsAhead = current.getDate() <= deadlineDay ? 1 : 2;
    const raw = currentMonth + monthsAhead;
    const tMonth = raw > 12 ? raw - 12 : raw;
    const tYear = raw > 12 ? currentYear + 1 : currentYear;
    return { year: tYear, month: tMonth, isExpired: false };
  }, [settings]);

  const isExpired = computedTarget.isExpired;

  useEffect(() => {
    const init = async () => {
      if (!supabaseClient) { setLoading(false); return; }
      const [empRes, settingsRes] = await Promise.all([
        supabaseClient.from('employees').select('id, name').eq('status', 'active').order('name'),
        supabaseClient.from('shift_settings').select('*').single(),
      ]);
      if (empRes.data) setEmployees(empRes.data);
      if (settingsRes.data) setSettings(settingsRes.data as ShiftSettings);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (!selectedEmployeeId || !supabaseClient) {
      setPreferences(new Map());
      setInitialLoaded(false);
      return;
    }
    const client = supabaseClient;
    const load = async () => {
      const { data } = await client
        .from('shift_preferences')
        .select('*')
        .eq('employee_id', selectedEmployeeId)
        .eq('target_year', computedTarget.year)
        .eq('target_month', computedTarget.month);
      const map = new Map<string, PreferenceType>();
      if (data) {
        data.forEach((row: Record<string, unknown>) => {
          const pref = toShiftPreference(row);
          map.set(pref.preferenceDate, pref.preferenceType);
        });
      }
      setPreferences(map);
      setInitialLoaded(true);
    };
    load();
  }, [selectedEmployeeId, computedTarget.year, computedTarget.month]);

  const daysInMonth = getDaysInMonth(computedTarget.year, computedTarget.month);
  const firstDow = getFirstDayOfWeek(computedTarget.year, computedTarget.month);

  const calendarCells = useMemo(() => {
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [firstDow, daysInMonth]);

  const togglePreference = useCallback((day: number) => {
    if (isExpired) return;
    const dateStr = `${computedTarget.year}-${String(computedTarget.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setPreferences(prev => {
      const next = new Map(prev);
      const current = next.get(dateStr) ?? null;
      const idx = PREFERENCE_CYCLE.indexOf(current);
      const nextType = PREFERENCE_CYCLE[(idx + 1) % PREFERENCE_CYCLE.length];
      if (nextType === null) {
        next.delete(dateStr);
      } else {
        next.set(dateStr, nextType);
      }
      return next;
    });
    setSaveResult(null);
  }, [computedTarget, isExpired]);

  const handleSave = async () => {
    if (!supabaseClient || !selectedEmployeeId || isExpired) return;
    setSaving(true);
    setSaveResult(null);

    const { error: delError } = await supabaseClient
      .from('shift_preferences')
      .delete()
      .eq('employee_id', selectedEmployeeId)
      .eq('target_year', computedTarget.year)
      .eq('target_month', computedTarget.month);

    if (delError) { setSaving(false); setSaveResult('error'); return; }

    if (preferences.size > 0) {
      const rows = Array.from(preferences.entries()).map(([date, type]) => ({
        employee_id: selectedEmployeeId,
        target_year: computedTarget.year,
        target_month: computedTarget.month,
        preference_date: date,
        preference_type: type,
      }));
      const { error: insError } = await supabaseClient.from('shift_preferences').insert(rows);
      if (insError) { setSaving(false); setSaveResult('error'); return; }
    }

    setSaving(false);
    setSaveResult('success');
    setTimeout(() => setSaveResult(null), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!supabaseClient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <p className="text-red-600">システムに接続できません。管理者にお問い合わせください。</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-teal-600 text-white shadow-md">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-6 h-6" />
            <h1 className="text-lg font-bold">シフト希望入力</h1>
          </div>
          <p className="text-teal-100 text-sm mt-1">
            {computedTarget.year}年{computedTarget.month}月分
          </p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {isExpired && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-red-700 text-sm">
              {computedTarget.year}年{computedTarget.month}月分の希望入力は締め切りました（毎月{settings?.deadline_day}日まで）
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">従業員を選択</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            value={selectedEmployeeId}
            onChange={e => { setSelectedEmployeeId(e.target.value); setSaveResult(null); }}
          >
            <option value="">-- 選択してください --</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        </div>

        {selectedEmployeeId && (
          <>
            <div className="flex flex-wrap gap-3 text-xs">
              {(Object.entries(PREFERENCE_TYPE_LABELS) as [PreferenceType, string][]).map(([type, label]) => (
                <span key={type} className={`px-2 py-1 rounded border ${PREFERENCE_STYLES[type]}`}>
                  {label}
                </span>
              ))}
              <span className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-500">なし</span>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-7">
                {DOW_LABELS.map((label, i) => (
                  <div key={label} className={`text-center text-xs font-semibold py-2 bg-gray-50 border-b border-gray-200 ${DOW_COLORS[i]}`}>
                    {label}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {calendarCells.map((day, i) => {
                  if (day === null) {
                    return <div key={`empty-${i}`} className="aspect-square border-b border-r border-gray-100" />;
                  }
                  const dateStr = `${computedTarget.year}-${String(computedTarget.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const pref = preferences.get(dateStr) ?? null;
                  const dow = (firstDow + day - 1) % 7;
                  const dayColor = dow === 0 ? 'text-red-500' : dow === 6 ? 'text-blue-500' : 'text-gray-800';
                  const cellBg = pref ? PREFERENCE_STYLES[pref].split(' ')[0] : '';

                  return (
                    <button
                      key={day}
                      disabled={isExpired}
                      onClick={() => togglePreference(day)}
                      className={`aspect-square border-b border-r border-gray-100 flex flex-col items-center justify-center gap-0.5 transition-colors
                        ${cellBg} ${isExpired ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50 active:bg-gray-100 cursor-pointer'}`}
                    >
                      <span className={`text-sm font-medium ${dayColor}`}>{day}</span>
                      {pref && (
                        <span className="text-[10px] leading-tight font-medium">
                          {PREFERENCE_TYPE_LABELS[pref]}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center">日付をタップすると 有給 → 公休 → 夜勤希望 → なし の順に切り替わります</p>

            {!isExpired && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                {saving ? '送信中...' : '希望を送信'}
              </button>
            )}

            {saveResult === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                <p className="text-green-700 text-sm">シフト希望を保存しました</p>
              </div>
            )}
            {saveResult === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                <p className="text-red-700 text-sm">保存に失敗しました。もう一度お試しください。</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ShiftRequest;
